import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import Loader from "../../components/loader";
import { useTranslation } from "react-i18next";
import ExpandableSection from "../../components/expandable-collapsible/component";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_ROMANTIC_COMPATIBILITY,
  MODEL_ROMANTIC_PERSONALIZED,
  PERSONALITY_TEST_ASSESSMENT_ID,
  ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
} from "../../utils/general-config";
import {
  getCookie,
  getEnneagramBannerImage,
  getRomanticCompatibilityImage,
} from "../../utils/utility-functions";
import {
  APP_NAVIGATION,
  LOCALE,
  ROMANTIC_COMPATIBILITY_REPORT_TYPE,
} from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

const RomanticCompatibilityReport = ({
  funnelStep,
  getInsightfulContentConnect,
  getUserAssessmentResultByResultIdConnect,
  sendToFriendReportOnEmailConnect,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [contentData, setContentData] = useState({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [userTraits, setUserTraits] = useState([]);
  const { locale } = useParams();
  const currentLocale = locale || LOCALE.EN;
  const { t } = useTranslation();
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const token = getCookie("jwtToken");
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID } = funnelStep || {};
  const userId = queryParams.get("uid") || userGUID;
  const assessmentAttemptResultId =
    queryParams.get("assessresid") || location.pathname.split("/").pop();
  const [userData, setUserData] = useState(null);

  const compatibilitystyle = "relationship-compatibility";
  const compatibilityImage = getRomanticCompatibilityImage(compatibilitystyle);

  function getCompatibilityLevel(scorePercent) {
    if (scorePercent >= 80) {
      return {
        englishCode: "High Compatibility",
        localizedCode: t("report.romantic-compatibility.high"),
      };
    }
    if (scorePercent >= 60 && scorePercent < 80) {
      return {
        englishCode: "Moderate Compatibility",
        localizedCode: t("report.romantic-compatibility.moderate"),
      };
    }
    return {
      englishCode: "Low Compatibility",
      localizedCode: t("report.romantic-compatibility.low"),
    };
  }

  useEffect(() => {
    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Get assessmentAttemptId from URL params or query params
        const searchParams = new URLSearchParams(location.search);
        const assessmentAttemptResultId =
          searchParams.get("assessresid") || location.pathname.split("/").pop();

        if (assessmentAttemptResultId) {
          // Fetch user assessment result from API
          const userAssessmentResponse =
            await getUserAssessmentResultByResultIdConnect(
              assessmentAttemptResultId,
              currentLocale
            );

          if (userAssessmentResponse && userAssessmentResponse.userTraits) {
            setUserTraits(userAssessmentResponse.userTraits);
          }
          // Minimal data needed by share/send flows
          setCurrentAssessmentAttemptData({
            assessmentId: ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
            ...userAssessmentResponse,
            assessmentAttemptId: assessmentAttemptResultId,
          });
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_ROMANTIC_COMPATIBILITY[currentLocale] ||
            MODEL_ROMANTIC_COMPATIBILITY[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [
    getUserAssessmentResultByResultIdConnect,
    getInsightfulContentConnect,
    currentLocale,
    location.search,
    location.pathname,
    assessmentAttemptResultId,
  ]);

  const bannerImage = getEnneagramBannerImage();

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };
  const HeroBanner = ({ title, color = "purple" }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent z-5`}
        />
        <div className="absolute inset-0 bg-black opacity-10 z-5" />

        <div className="relative h-full flex items-start">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const ProgressCircle = ({
    color = "#2563EB",
    duration = 2000,
    targetPercentage = 50,
  }) => {
    const [percentage, setPercentage] = useState(0);
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const intervalTime = 30; // update interval

    useEffect(() => {
      const steps = duration / intervalTime;
      const stepSize = targetPercentage / steps;

      let currentPercentage = 0;
      const interval = setInterval(() => {
        currentPercentage += stepSize;
        if (currentPercentage >= targetPercentage) {
          currentPercentage = targetPercentage;
          clearInterval(interval);
        }
        setPercentage(parseInt(currentPercentage));
      }, intervalTime);

      return () => clearInterval(interval);
    }, [duration, targetPercentage]);

    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%]">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Animated Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 0.1s ease-in-out" }}
            />
          </svg>

          {/* Center Data Only */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-3xl font-bold text-blue-600">
              {percentage}%
            </div>
          </div>
        </div>
      </div>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div className="grid grid-cols-1 lg:grid-cols gap-4 items-start mb-5">
        <div
          className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
        >
          {heading && (
            <div className="flex items-start gap-2 mb-2">
              <div>
                <div className="flex items-start gap-2">
                  {character && (
                    <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                      {" "}
                    </div>
                  )}

                  <h2
                    className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                  >
                    {heading}
                  </h2>
                </div>
              </div>
            </div>
          )}

          {overview && (
            <p
              className={`text-lg text-gray-700 mt-4 ${
                character ? "pl-8 md:pl-10" : ""
              } ${contentArray ? `mb-4` : ""}`}
            >
              {overview}
            </p>
          )}
          {contentArray && (
            <ul
              className={`space-y-2 mt-4 ${
                overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
              }`}
            >
              {contentArray?.map((item, index) => {
                return (
                  <li key={index} className="flex gap-4">
                    {/* Right Section: Heading & Content */}
                    <div className="flex flex-col">
                      {isSubHeading ? (
                        <>
                          {/* Heading and First Line in Same Row */}
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-700">
                              {item?.heading}
                            </h2>
                          </div>

                          {/* Paragraph with text-indent effect */}
                          <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                            <span className="">{item.content}</span>
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {item}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  };
  const renderCompatibilityCards = () => {
    return userTraits.map((traitObj) => {
      const traitTitle = traitObj.trait.title; // Get the trait title from the nested structure
      const percentage = traitObj.percentage;

      // TRAIT_KEY_MAP to match your data structure
      const TRAIT_KEY_MAP = {
        overall: "Overall",
        communication_style: "CommunicationStyle",
        emotional_compatibility: "EmotionalCompatibility",
        lifestyle_preferences: "LifestylePreferences",
        conflict_resolution: "ConflictResolution",
        core_values_and_beliefs: "CoreValues",
        intimacy_and_affection: "IntimacyAffection",
        independence_vs_togetherness: "IndependenceTogetherness",
        financial_philosophy: "FinancialPhilosophy",
        ambition_and_growth: "AmbitionGrowth",
        family_and_social_dynamics: "FamilySocialDynamics",
        adventure_and_exploration: "AdventureExploration",
        conflict_triggers: "ConflictTriggers",
      };

      const getLevelSuffix = (percentage) => {
        if (percentage >= 80) return "H";
        if (percentage >= 61) return "M";
        return "L";
      };

      const baseKey = TRAIT_KEY_MAP[traitTitle];
      if (!baseKey) {
        console.warn(`No trait mapping: ${traitTitle}`);
        return null;
      }

      const suffix = getLevelSuffix(percentage); // Just to show background/text colors
      const keyWithSuffix = `${baseKey}_${suffix}`;

      // Access the content object from your JSON structure
      const traitData = contentData?.[keyWithSuffix] || {};

      const fullName = traitData.FullName || `${traitTitle}`;
      const scoreExplanation = traitData.ScoreExplanation || "";
      const strengths = traitData.Strengths || "";
      const challenges = traitData.Challenges || "";
      const immediateFocusAreas = traitData.ImmediateFocusAreas || [];
      const longTermGoals = traitData.LongTermGoals || [];

      // Get headings from the root content level
      const immediateFocusHeading =
        contentData?.ImmediateFocusHeading || "Immediate Focus Areas";
      const longTermGoalsHeading =
        contentData?.LongTermGoalsHeading || "Long-Term Development Goals";

      const bgColorByLevel = {
        H: "#FFE5F0",
        M: "#FFF3E0",
        L: "#FFFCE0",
      };

      const textColorByLevel = {
        H: "#d22f65ff", // darker pink
        M: "#B85C00", // darker orange
        L: "#B89B00", // darker yellow
      };

      return (
        <div>
          <div
            key={traitObj.id}
            style={{ backgroundColor: bgColorByLevel[suffix] }}
            className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full w-full break-words overflow-hidden"
          >
            {/* Full Name */}
            <h2
              className="text-lg sm:text-xl font-semibold flex items-center mb-2 break-words min-w-0 pr-3 sm:pr-0" // 🔹 added break-words & min-w-0
              style={{ color: textColorByLevel[suffix] }}
            >
              {fullName}
            </h2>

            {/* Score Explanation */}
            <div className="mb-4">
              <p className="text-gray-700 text-lg mt-4 md:text-lg leading-relaxed relative">
                {scoreExplanation}
              </p>
            </div>
            {/*Strengths*/}
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                {t("report.romantic-compatibility.strengths")}
              </h3>
              <p className="text-gray-700 text-lg mt-4 md:text-lg leading-relaxed relative">
                {strengths}
              </p>
            </div>
            {/* Challenges */}
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                {t("report.romantic-compatibility.challenges")}
              </h3>
              <p className="text-gray-700 text-lg mt-4 md:text-lg leading-relaxed relative">
                {challenges}
              </p>
            </div>

            {/* Immediate Focus Areas */}
            {immediateFocusAreas.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                  {immediateFocusHeading}
                </h3>
                <div className="space-y-3 mt-auto pr-3 sm:pr-0">
                  {immediateFocusAreas.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 leading-relaxed"
                    >
                      {/* Custom SVG Icon */}
                      <div className="w-6 h-6 flex-shrink-0">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <defs>
                            <filter
                              id="shadow"
                              x="-20%"
                              y="-20%"
                              width="140%"
                              height="140%"
                            >
                              <feDropShadow
                                dx="0"
                                dy="2"
                                stdDeviation="3"
                                floodColor="black"
                                floodOpacity="0.4"
                              />
                            </filter>
                          </defs>
                          <path
                            d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                            fill="#346bcc"
                            stroke="#fff"
                            strokeWidth="6"
                            filter="url(#shadow)"
                          />
                        </svg>
                      </div>
                      {/* 🔹 Added min-w-0 so text can shrink inside flex */}
                      <span className="break-words min-w-0">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Long Term Development Goals */}
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                {longTermGoalsHeading}
              </h3>
              {longTermGoals.length > 0 && (
                <div className="space-y-2 mb-4 pl-1 pr-3 sm:pr-0">
                  {longTermGoals.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 leading-relaxed"
                    >
                      {/* Custom SVG Icon */}
                      <div className="w-6 h-6 flex-shrink-0">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <defs>
                            <filter
                              id="shadow"
                              x="-20%"
                              y="-20%"
                              width="140%"
                              height="140%"
                            >
                              <feDropShadow
                                dx="0"
                                dy="2"
                                stdDeviation="3"
                                floodColor="black"
                                floodOpacity="0.4"
                              />
                            </filter>
                          </defs>
                          <path
                            d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                            fill="#346bcc"
                            stroke="#fff"
                            strokeWidth="6"
                            filter="url(#shadow)"
                          />
                        </svg>
                      </div>
                      {/* 🔹 Added min-w-0 */}
                      <span className="break-words min-w-0">{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  };
  const renderOverallResultsCard = () => {
    if (!userTraits || userTraits.length === 0) {
      return null;
    }

    // Calculate overall compatibility percentage - sum of all trait percentages divided by 12
    const overallPercentage = Math.round(
      userTraits.reduce((sum, trait) => sum + trait.percentage, 0) / 12
    );

    const getLevelSuffix = (percentage) => {
      if (percentage >= 80) return "H";
      if (percentage >= 61) return "M";
      return "L";
    };

    const suffix = getLevelSuffix(overallPercentage);
    const keyWithSuffix = `Overall_${suffix}`;

    // Access the content object from your JSON structure
    const traitData = contentData?.[keyWithSuffix] || {};

    const scoreExplanation = traitData.ScoreExplanation || "";

    // Handle cases where data might be strings instead of arrays
    const formatArrayData = (data) => {
      if (Array.isArray(data)) return data;
      if (typeof data === "string" && data.trim()) return [data];
      if (typeof data === "object" && data !== null) return Object.values(data);
      return [];
    };

    const keyStrengths = formatArrayData(traitData.KeyStrengths);
    const areasForGrowth = formatArrayData(traitData.AreasForGrowth);
    const relationshipOutlook = traitData.RelationshipOutlook || "";

    return (
      <div className="grid grid-cols-1 lg:grid-cols-[50%_50%] gap-4 items-start mb-5 w-full">
        {/* Left Section - Content */}
        <div className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col break-words overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
          {/* Header */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4 text-left">
            {contentData?.OverallHeading || "Overall Compatibility Results"}
          </h2>

          {/* Score Explanation */}
          {scoreExplanation && (
            <div className="mb-4">
              <p className="text-gray-700 text-base leading-relaxed">
                {scoreExplanation}
              </p>
            </div>
          )}

          {/* Key Strengths */}
          {keyStrengths.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {t("report.romantic-compatibility.key_strengths")}
              </h3>
              <ul className="space-y-2">
                {keyStrengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Areas for Growth */}
          {areasForGrowth.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {t("report.romantic-compatibility.areas_for_growth")}
              </h3>
              <ul className="space-y-2">
                {areasForGrowth.map((area, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Relationship Outlook */}
          {relationshipOutlook && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {t("report.romantic-compatibility.relationship_outlook")}
              </h3>
              <p className="text-gray-700 text-base leading-relaxed">
                {relationshipOutlook}
              </p>
            </div>
          )}
        </div>

        {/* Image Section (Smaller) */}
        <div className="p-4 md:p-6 flex flex-col break-words overflow-hidden">
          <img
            src={compatibilityImage}
            alt="BannerImage"
            className="w-full object-contain"
          />
        </div>
      </div>
    );
  };
  const CallToActionSection = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {contentData.CTAPersonalGrowth}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t(
                "report.romantic-compatibility.discover_more_about_your_personality_type"
              )}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };
  if (isLoading) {
    return <Loader />;
  }
  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner
                      title={t(
                        "report.romantic-compatibility.compatibility_heading"
                      )}
                      color="blue"
                    />
                  }
                >
                  {token && (
                    <div className="w-full pt-2 flex justify-end gap-2 px-4 lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userId}
                        userData={userData}
                        isRomanticCompatibility={true}
                        assessmentAttemptResultId={assessmentAttemptResultId}
                        assessmentId={ROMANTIC_COMPATIBILITY_ASSESSMENT_ID}
                      />
                      <SendToFriendButton
                        userGUID={userId}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                        isRomanticCompatibility={true}
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        reportType={
                          ROMANTIC_COMPATIBILITY_REPORT_TYPE.COMPATIBILITY
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[50%_50%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                          {contentData?.Intro?.RootHeading}
                        </h2>
                        <h2 className="text-lg text-gray-600 mb-4 whitespace-pre-line">
                          {contentData?.Intro?.Content}
                        </h2>
                      </div>
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal text-center">
                          {t("report.romantic-compatibility.over_score")}
                        </h2>
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal text-center">
                          {userTraits.length > 0
                            ? getCompatibilityLevel(
                                Math.round(
                                  userTraits.reduce(
                                    (sum, trait) => sum + trait.percentage,
                                    0
                                  ) / userTraits.length
                                )
                              ).localizedCode
                            : ""}
                        </h2>

                        {/* Right Section: Circle Score Graph */}
                        <div className="flex items-center h-[100%] justify-center">
                          <ProgressCircle
                            targetPercentage={
                              userTraits.length > 0
                                ? Math.round(
                                    userTraits.reduce(
                                      (sum, trait) => sum + trait.percentage,
                                      0
                                    ) / userTraits.length
                                  )
                                : 0
                            }
                            color="#2563EB"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t(
                        "report.romantic-compatibility.relationship_action_plan"
                      )}
                      color="blue"
                    />
                  }
                >
                  <div>
                    <div className="mb-8">{renderOverallResultsCard()}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                      {renderCompatibilityCards()}
                    </div>
                  </div>
                  <DescriptiveCard
                    heading={contentData?.DisclaimerHeading}
                    overview={contentData?.Disclaimer}
                    bgClr={"bg-green-50"}
                    textClr={"text-green-800"}
                  />
                </ExpandableSection>
              </section>

              <CallToActionSection data={contentData} />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RomanticCompatibilityReport;
